/**
 * Submits the wrapped form to a Coast Files endpoint with fetch.
 * Coast Files answers with JSON `{ success, message, redirect_url? }`; `message` is safe to show.
 * With `data-turnstile-sitekey`, a Cloudflare Turnstile widget renders into `[data-coast-turnstile]`
 * and its token is sent as `cf-turnstile-response`. Tokens are single-use, so it resets after every send.
 */

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let turnstileReady;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileReady) {
    turnstileReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        turnstileReady = null;
        reject(new Error('Turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return turnstileReady;
}

class PaCoastForm extends HTMLElement {
  connectedCallback() {
    if (this.form) return;
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.errorRegion = this.querySelector('[data-coast-error]');
    this.statusRegion = this.querySelector('[data-coast-status]');
    this.success = this.querySelector('[data-coast-success]');
    this.turnstileContainer = this.querySelector('[data-coast-turnstile]');

    this.onSubmit = (event) => this.submit(event);
    this.onResetClick = (event) => {
      event.preventDefault();
      this.showForm();
    };

    this.form.addEventListener('submit', this.onSubmit);
    this.querySelectorAll('[data-coast-reset]').forEach((control) => {
      control.addEventListener('click', this.onResetClick);
    });

    this.renderTurnstile();
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('submit', this.onSubmit);
    this.querySelectorAll('[data-coast-reset]').forEach((control) => {
      control.removeEventListener('click', this.onResetClick);
    });
    if (this.turnstileId !== undefined && window.turnstile) window.turnstile.remove(this.turnstileId);
    this.turnstileId = undefined;
    this.form = null;
  }

  async renderTurnstile() {
    const sitekey = this.dataset.turnstileSitekey;
    if (!sitekey || !this.turnstileContainer) return;
    try {
      const turnstile = await loadTurnstile();
      if (!this.form || this.turnstileId !== undefined) return;
      this.turnstileId = turnstile.render(this.turnstileContainer, {
        sitekey,
        size: 'flexible',
        theme: 'light',
      });
    } catch (error) {
      this.setError(this.dataset.turnstileError || this.dataset.errorMessage || '');
    }
  }

  turnstileToken() {
    if (this.turnstileId === undefined || !window.turnstile) return '';
    return window.turnstile.getResponse(this.turnstileId) || '';
  }

  resetTurnstile() {
    if (this.turnstileId !== undefined && window.turnstile) window.turnstile.reset(this.turnstileId);
  }

  async submit(event) {
    event.preventDefault();
    if (this.form.getAttribute('aria-busy') === 'true') return;

    const fallback = this.dataset.errorMessage || '';
    if (this.dataset.turnstileSitekey && this.turnstileContainer && !this.turnstileToken()) {
      this.setError(this.dataset.turnstilePending || fallback);
      return;
    }

    this.setError('');
    this.setBusy(true);

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
      });
      const result = await response.json().catch(() => ({ success: false }));
      if (!response.ok || !result.success) {
        this.setError(result.message || fallback);
        return;
      }
      if (result.redirect_url) {
        window.location.assign(result.redirect_url);
        return;
      }
      this.showSuccess();
    } catch (error) {
      this.setError(fallback);
    } finally {
      this.setBusy(false);
      this.resetTurnstile();
    }
  }

  setBusy(busy) {
    this.form.setAttribute('aria-busy', String(busy));
    if (this.submitButton) this.submitButton.disabled = busy;
    const label = this.form.querySelector('[data-coast-label]');
    if (label) {
      if (this.idleLabel === undefined) this.idleLabel = label.textContent;
      label.textContent = busy ? this.dataset.sendingMessage || this.idleLabel : this.idleLabel;
    }
    if (this.statusRegion) this.statusRegion.textContent = busy ? this.dataset.sendingMessage || '' : '';
  }

  reveal(element) {
    element.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  setError(message) {
    if (!this.errorRegion) return;
    this.errorRegion.textContent = message;
    this.errorRegion.hidden = !message;
    if (message) this.reveal(this.errorRegion);
  }

  showSuccess() {
    this.form.reset();
    if (this.hasAttribute('hide-form-on-success')) this.form.hidden = true;
    if (!this.success) return;
    this.success.hidden = false;
    this.reveal(this.success);
  }

  showForm() {
    if (this.success) this.success.hidden = true;
    this.form.hidden = false;
    const first = this.form.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea, select');
    if (first) first.focus();
  }
}

if (!customElements.get('pa-coast-form')) {
  customElements.define('pa-coast-form', PaCoastForm);
}
